import { useState } from 'react';
import { ArrowRight, Brain, CheckCircle2, Flame, Sparkles, Sprout, Star } from 'lucide-react';
import { useTodaysChallenge } from '@/lib/challengeQueries';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChallengeDialog } from './ChallengeDialog';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TODAY'S LEARNING SPARK
 *
 * Three parts across: a coloured rail that names the thing, the habit on the
 * left, today's question on the right.
 *
 * The split is the point. The left half is about the streak and never changes
 * — it is there whether or not there is a question today. The right half is
 * the only part that varies: ready, done, or nothing scheduled. Putting the
 * streak beside the call to action rather than inside it means a day with no
 * challenge still has something true and encouraging to say.
 *
 * On a day nobody scheduled a question the card does NOT hide and does not
 * apologise. A missed day only breaks the run if there was something to miss.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export function LearningSpark() {
  const { data, isLoading } = useTodaysChallenge();
  const [open, setOpen] = useState(false);

  // Nothing at all rather than a skeleton: the dashboard has enough on it
  // without a placeholder for a card that may turn out to be empty.
  if (isLoading || !data) return null;

  const { challenge, streak, learningStreak } = data;
  const answered = Boolean(challenge?.myAnswer);

  // On a day with nothing to answer, a challenge streak that cannot move is
  // noise — the streak still moving is the one worth showing.
  const days = challenge ? streak.current : learningStreak;
  const streakLabel = challenge ? 'day streak' : 'day learning streak';

  return (
    <>
      <Card className="overflow-hidden p-0">
        <div className="flex flex-col sm:flex-row">
          {/*
            ── The rail ───────────────────────────────────────────────

            `brand-800` — the same green as the selected item in the sidebar.

            That is the app's one "this is active" colour, and the rail is the
            only other block of solid green on the dashboard. A shade off it
            reads as a mistake rather than a choice, because the two sit in the
            same eyeline.
          */}
          <div className="flex shrink-0 items-center gap-3 bg-brand-800 px-4 py-3 text-white sm:w-[92px] sm:flex-col sm:justify-center sm:gap-2 sm:px-2 sm:py-6">
            <Brain className="h-6 w-6 shrink-0" aria-hidden />
            <p className="text-[10px] font-semibold uppercase leading-tight tracking-wider text-white/80 sm:text-center">
              Daily
              <br className="hidden sm:inline" />{' '}
              Learning
              <br className="hidden sm:inline" />{' '}
              Moment
            </p>
          </div>

          {/* ── The habit ────────────────────────────────────────────── */}
          <div className="min-w-0 flex-1 px-5 py-4">
            <h2 className="text-[15px] font-semibold">Keep the learning habit alive!</h2>
            <p className="mt-0.5 text-[13px] leading-snug text-muted-foreground">
              A quick question every day helps you grow consistently.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/* Hidden at zero rather than shown as "0 day streak", which
                  reads as a telling-off on somebody's first morning. */}
              {days > 0 && (
                /**
                 * Solid, not a wash.
                 *
                 * This was `bg-warning/15` with `text-warning-foreground`,
                 * and that foreground is WHITE in light mode — so it was
                 * white text on a pale orange tint, all but unreadable. A
                 * streak is the one number here somebody actually wants to
                 * check, so it gets the filled treatment and full contrast.
                 */
                <span className="flex items-center gap-1.5 rounded-full bg-warning px-3 py-1 text-xs font-semibold text-warning-foreground">
                  <Flame className="h-3.5 w-3.5" aria-hidden />
                  {days} {streakLabel}
                </span>
              )}
              {/* Only once it means something. A best that equals the current
                  run is the same number twice. */}
              {challenge && streak.best > streak.current && (
                <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                  Best: {streak.best} days
                </span>
              )}
            </div>
          </div>

          {/* ── Today ────────────────────────────────────────────────── */}
          <div className="flex w-full flex-col justify-center gap-2.5 border-t px-5 py-4 sm:w-[300px] sm:shrink-0 sm:border-l sm:border-t-0">
            {challenge ? (
              answered ? (
                <>
                  <p className="flex items-center gap-2 text-[15px] font-semibold">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
                    Today’s challenge complete
                  </p>
                  <p className="text-[13px] text-muted-foreground">
                    {challenge.myAnswer!.isCorrect
                      ? 'You got it right. Back tomorrow for the next one.'
                      : 'Worth another look at the explanation.'}
                  </p>
                  <Button variant="outline" onClick={() => setOpen(true)}>
                    See the answer
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-[15px] font-semibold">Today’s challenge is ready!</p>

                  <span className="inline-flex w-fit items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5" aria-hidden />
                    Topic: {challenge.topic}
                  </span>

                  <Button onClick={() => setOpen(true)} className="justify-between">
                    Start challenge
                    <ArrowRight className="h-4 w-4" />
                  </Button>

                  <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Star className="h-3.5 w-3.5 fill-warning text-warning" aria-hidden />
                    <span className="font-medium text-foreground">+10 points</span> for a correct
                    answer
                  </p>
                </>
              )
            ) : (
              /**
               * No challenge today.
               *
               * Not an apology and not an empty space — the streak is not at
               * risk, because a day with nothing published cannot break it.
               */
              <>
                <p className="flex items-center gap-2 text-[15px] font-semibold">
                  <Sprout className="h-4 w-4 shrink-0 text-success" aria-hidden />
                  No challenge today
                </p>
                <p className="text-[13px] leading-snug text-muted-foreground">
                  Take a breather — your next learning spark will be ready tomorrow. Your completed
                  learning still counts toward your progress.
                </p>
              </>
            )}
          </div>
        </div>
      </Card>

      {challenge && (
        <ChallengeDialog
          challenge={challenge}
          open={open}
          onClose={() => setOpen(false)}
          // Passed down rather than refetched: the card and the dialog show
          // the same streak, so they must be the same streak.
          streak={streak}
          stats={data.stats}
        />
      )}
    </>
  );
}
